import React, { useEffect, useRef, useState } from 'react';
import {
    IoMic, IoMicOff, IoVideocam, IoVideocamOff, IoDesktop, IoDesktopOutline,
    IoHandLeft, IoHandLeftOutline, IoHappyOutline, IoChatbubbleEllipses, IoPeople,
    IoEllipsisVertical, IoChevronUp, IoCall, IoGrid, IoStop, IoDocumentText, IoColorWandOutline,
    IoSpeedometerOutline
} from 'react-icons/io5';
import MeetingBackgroundPicker from './MeetingBackgroundPicker';
import { VIDEO_QUALITIES } from '../../hooks/useVideoQuality';
import { useLanguage } from '../../i18n/LanguageContext';

// Matches the set Meet offers; deliberately small, since this renders as a
// row of buttons rather than a full picker.
const REACTIONS = ['👍', '🎉', '👏', '❤️', '😂', '😮', '😢', '🤔'];

// A device chooser that hangs off the little chevron on the mic/camera button,
// instead of hiding input selection behind a settings screen - switching
// headsets mid-call is a thing people do.
function DeviceMenu({ open, devices, selectedId, onSelect, emptyLabel }) {
    if (!open) return null;
    return (
        <div className="MeetDeviceMenu glass">
            {devices.length === 0 && <span className="MeetDeviceEmpty">{emptyLabel}</span>}
            {devices.map((device, index) => (
                <button
                    key={device.deviceId || index}
                    className={`MeetDeviceItem ${device.deviceId === selectedId ? 'MeetDeviceItemActive' : ''}`}
                    onClick={() => onSelect(device.deviceId)}
                >
                    {/* Labels are empty until the matching permission has been
                        granted - enumerateDevices() does not request one. */}
                    {device.label || `${emptyLabel} ${index + 1}`}
                </button>
            ))}
        </div>
    );
}

export default function MeetingControlBar({
    muted, onToggleMute, audioDevices, selectedAudioDeviceId, onSelectAudioDevice,
    cameraOn, onToggleCamera, videoDevices, selectedVideoDeviceId, onSelectVideoDevice,
    screenShareOn, onToggleScreenShare, screenShareAvailable,
    handRaised, onToggleHand, onReaction,
    background, onSelectBackground, sharedScene, onShareScene,
    chatOpen, onToggleChat, unreadCount,
    participantsOpen, onToggleParticipants, participantCount,
    transcriptOpen, onToggleTranscriptPanel, transcriptStatus,
    videoQuality,
    pinned, onClearPin,
    isHost, onEndMeeting, onLeave
}) {
    const { t } = useLanguage();
    const [openMenu, setOpenMenu] = useState(null); // 'mic' | 'camera' | 'reactions' | 'more'
    const barRef = useRef(null);

    useEffect(() => {
        if (!openMenu) return undefined;
        const close = (event) => {
            if (barRef.current && !barRef.current.contains(event.target)) setOpenMenu(null);
        };
        document.addEventListener('mousedown', close, true);
        return () => document.removeEventListener('mousedown', close, true);
    }, [openMenu]);

    const toggleMenu = (name) => setOpenMenu((current) => (current === name ? null : name));

    return (
        <div className="MeetControlBar" ref={barRef}>
            <div className="MeetControlGroup">
                <div className="MeetControlWithMenu">
                    <button
                        className={`MeetRoundBtn ${muted ? 'MeetRoundBtnDanger' : ''}`}
                        onClick={onToggleMute}
                        title={muted ? t('meeting.micOn') : t('meeting.micOff')}
                    >
                        {muted ? <IoMicOff /> : <IoMic />}
                    </button>
                    <button className="MeetChevronBtn" onClick={() => toggleMenu('mic')} title={t('meeting.audioDevices')}>
                        <IoChevronUp />
                    </button>
                    <DeviceMenu
                        open={openMenu === 'mic'}
                        devices={audioDevices}
                        selectedId={selectedAudioDeviceId}
                        onSelect={(id) => { onSelectAudioDevice(id); setOpenMenu(null); }}
                        emptyLabel={t('meeting.microphone')}
                    />
                </div>

                <div className="MeetControlWithMenu">
                    <button
                        className={`MeetRoundBtn ${cameraOn ? '' : 'MeetRoundBtnDanger'}`}
                        onClick={onToggleCamera}
                        title={cameraOn ? t('meeting.cameraOff') : t('meeting.cameraOn')}
                    >
                        {cameraOn ? <IoVideocam /> : <IoVideocamOff />}
                    </button>
                    <button className="MeetChevronBtn" onClick={() => toggleMenu('camera')} title={t('meeting.videoDevices')}>
                        <IoChevronUp />
                    </button>
                    <DeviceMenu
                        open={openMenu === 'camera'}
                        devices={videoDevices}
                        selectedId={selectedVideoDeviceId}
                        onSelect={(id) => { onSelectVideoDevice(id); setOpenMenu(null); }}
                        emptyLabel={t('meeting.camera')}
                    />
                </div>

                {/* Hidden rather than disabled where the platform can't do it
                    at all (mobile browsers have no getDisplayMedia) - a button
                    that can only ever fail is noise. */}
                {screenShareAvailable && (
                    <button
                        className={`MeetRoundBtn ${screenShareOn ? 'MeetRoundBtnActive' : ''}`}
                        onClick={onToggleScreenShare}
                        title={screenShareOn ? t('meeting.stopShare') : t('meeting.share')}
                    >
                        {screenShareOn ? <IoDesktop /> : <IoDesktopOutline />}
                    </button>
                )}

                <div className="MeetControlWithMenu">
                    <button className="MeetRoundBtn" onClick={() => toggleMenu('reactions')} title={t('meeting.reactions')}>
                        <IoHappyOutline />
                    </button>
                    {openMenu === 'reactions' && (
                        <div className="MeetReactionMenu glass">
                            {REACTIONS.map((emoji) => (
                                <button
                                    key={emoji}
                                    className="MeetReactionBtn"
                                    onClick={() => { onReaction(emoji); setOpenMenu(null); }}
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Hidden where the browser cannot run the pipeline at all
                    (Firefox and Safari have no Insertable Streams), rather
                    than shown as a button that only ever reports failure. */}
                {background.supported && (
                    <div className="MeetControlWithMenu">
                        <button
                            className={`MeetRoundBtn ${background.effect.kind !== 'none' ? 'MeetRoundBtnActive' : ''}`}
                            onClick={() => toggleMenu('background')}
                            title={t('meeting.background.title')}
                        >
                            <IoColorWandOutline />
                        </button>
                        {openMenu === 'background' && (
                            <MeetingBackgroundPicker
                                effect={background.effect}
                                onSelect={onSelectBackground}
                                applying={background.applying}
                                isHost={isHost}
                                sharedScene={sharedScene}
                                onShareScene={onShareScene}
                            />
                        )}
                    </div>
                )}

                <button
                    className={`MeetRoundBtn ${handRaised ? 'MeetRoundBtnActive' : ''}`}
                    onClick={onToggleHand}
                    title={handRaised ? t('meeting.lowerHand') : t('meeting.raiseHand')}
                >
                    {handRaised ? <IoHandLeft /> : <IoHandLeftOutline />}
                </button>

                <div className="MeetControlWithMenu">
                    <button className="MeetRoundBtn" onClick={() => toggleMenu('more')} title={t('meeting.more')}>
                        <IoEllipsisVertical />
                    </button>
                    {openMenu === 'more' && (
                        <div className="MeetMoreMenu glass">
                            {/* Only the sending side is offered. What this
                                client receives is already chosen per tile by
                                adaptiveStream, which knows the on-screen size
                                and so can do it better than a menu can. */}
                            <div className="MeetMoreSection">
                                <span className="MeetMoreSectionTitle"><IoSpeedometerOutline /> {t('meeting.quality.title')}</span>
                                <div className="MeetQualityRow">
                                    {VIDEO_QUALITIES.map((level) => (
                                        <button
                                            key={level}
                                            className={`MeetQualityBtn ${videoQuality.quality === level ? 'MeetQualityBtnActive' : ''}`}
                                            onClick={() => videoQuality.selectQuality(level)}
                                            disabled={videoQuality.applying}
                                        >
                                            {t(`meeting.quality.${level}`)}
                                        </button>
                                    ))}
                                </div>
                                <p className="MeetMoreHint">{t('meeting.quality.hint')}</p>
                            </div>

                            {/* Only offered while something is actually pinned -
                                the stage falls back to auto layout (grid, or a
                                shared screen) the moment the pin is cleared. */}
                            {pinned && (
                                <button className="MeetMoreItem" onClick={() => { onClearPin(); setOpenMenu(null); }}>
                                    <IoGrid /> {t('meeting.backToGrid')}
                                </button>
                            )}
                            {isHost && (
                                <button className="MeetMoreItem MeetMoreDanger" onClick={() => { setOpenMenu(null); onEndMeeting(); }}>
                                    <IoStop /> {t('meeting.endForAll')}
                                </button>
                            )}

                        </div>
                    )}
                </div>

                <button className="MeetRoundBtn MeetLeaveBtn" onClick={onLeave} title={t('meeting.leave')}>
                    <IoCall />
                </button>
            </div>

            <div className="MeetControlRight">
                <button
                    className={`MeetPanelBtn ${chatOpen ? 'MeetPanelBtnActive' : ''}`}
                    onClick={onToggleChat}
                    title={t('meeting.chat')}
                >
                    <IoChatbubbleEllipses />
                    {unreadCount > 0 && !chatOpen && <span className="MeetUnreadDot">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </button>
                <button
                    className={`MeetPanelBtn ${participantsOpen ? 'MeetPanelBtnActive' : ''}`}
                    onClick={onToggleParticipants}
                    title={t('meeting.participants')}
                >
                    <IoPeople />
                    <span className="MeetPanelBtnCount">{participantCount}</span>
                </button>
                {/* Visible to everyone, not just the host: being able to see
                    that a conversation is being transcribed is the point. */}
                <button
                    className={`MeetPanelBtn ${transcriptOpen ? 'MeetPanelBtnActive' : ''}`}
                    onClick={onToggleTranscriptPanel}
                    title={t('meeting.transcript.tab')}
                >
                    <IoDocumentText />
                    {(transcriptStatus === 'active' || transcriptStatus === 'connecting') && <span className="MeetRecDot" />}
                </button>
            </div>
        </div>
    );
}
